import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Autocomplete
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Public as PublicIcon,
  Business as BusinessIcon
} from '@mui/icons-material'
import { categoryService } from '@shared/services/CategoryService'
import { OrganizationService } from '@shared/services/OrganizationService'
import { CategorySummary, Organization } from '@shared/types/entities'

function CategoryManagement() {
  const [categories, setCategories] = useState<CategorySummary[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CategorySummary | null>(null)
  const [organizationScope, setOrganizationScope] = useState<'global' | 'specific'>('global')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#2196f3',
    sortOrder: 0,
    childSiteIds: [] as string[]
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadCategories()
    loadOrganizations()
  }, [])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const data = await categoryService.getCategories()
      setCategories(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true)
      const data = await OrganizationService.getOrganizations()
      // Show all sites (including inactive) for category assignment
      setOrganizations(data)
    } catch (err: any) {
      console.error('Failed to load organizations:', err)
      // Don't show error - organizations are optional
    } finally {
      setLoadingOrgs(false)
    }
  }

  const handleOpenDialog = (category?: CategorySummary) => {
    if (category) {
      setEditingCategory(category)

      // Determine selected sites - use childSiteIds if available, otherwise fall back to childSiteId
      const selectedSites = category.childSiteIds && category.childSiteIds.length > 0
        ? category.childSiteIds
        : category.childSiteId
        ? [category.childSiteId]
        : []

      setOrganizationScope(selectedSites.length > 0 ? 'specific' : 'global')
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#2196f3',
        sortOrder: 0,
        childSiteIds: selectedSites
      })
    } else {
      setEditingCategory(null)
      setOrganizationScope('global')
      setFormData({
        name: '',
        description: '',
        color: '#2196f3',
        sortOrder: categories.length + 1,
        childSiteIds: []
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingCategory(null)
    setError(null)
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required')
      return
    }

    if (organizationScope === 'specific' && formData.childSiteIds.length === 0) {
      setError('Please select at least one site')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Set childSiteIds based on scope: undefined/empty for global, actual IDs for specific
      const childSiteIds = organizationScope === 'global' ? undefined : formData.childSiteIds

      if (editingCategory) {
        // Update existing category
        await categoryService.updateCategory(editingCategory.categoryId, {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          sortOrder: formData.sortOrder,
          isActive: true,
          childSiteIds
        })
        setSuccess('Category updated successfully')
      } else {
        // Create new category
        await categoryService.createCategory({
          name: formData.name,
          description: formData.description,
          color: formData.color,
          sortOrder: formData.sortOrder,
          isActive: true,
          childSiteIds
        })
        setSuccess('Category created successfully')
      }

      handleCloseDialog()
      loadCategories()

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to save category')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (category: CategorySummary) => {
    if (category.ticketCount && category.ticketCount > 0) {
      setError(`Cannot delete category "${category.name}" because it has ${category.ticketCount} associated tickets`)
      setTimeout(() => setError(null), 5000)
      return
    }

    if (!confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      return
    }

    try {
      await categoryService.deleteCategory(category.categoryId)
      setSuccess('Category deleted successfully')
      loadCategories()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to delete category')
      setTimeout(() => setError(null), 5000)
    }
  }

  const colorOptions = [
    { name: 'Red', value: '#f44336' },
    { name: 'Pink', value: '#e91e63' },
    { name: 'Purple', value: '#9c27b0' },
    { name: 'Blue', value: '#2196f3' },
    { name: 'Cyan', value: '#00bcd4' },
    { name: 'Teal', value: '#009688' },
    { name: 'Green', value: '#4caf50' },
    { name: 'Orange', value: '#ff9800' },
    { name: 'Grey', value: '#9e9e9e' }
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Category Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage ticket categories for your helpdesk. Categories help organise and classify support requests.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Category
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="center"><strong>Colour</strong></TableCell>
              <TableCell><strong>Scope</strong></TableCell>
              <TableCell align="center"><strong>Tickets</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No categories found. Click "Add Category" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.categoryId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {category.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {category.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <CircleIcon sx={{ color: category.color, fontSize: 24 }} />
                  </TableCell>
                  <TableCell>
                    {category.childSiteIds && category.childSiteIds.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {category.childSiteNames?.map((siteName, index) => (
                          <Chip
                            key={category.childSiteIds![index]}
                            icon={<BusinessIcon />}
                            label={siteName}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Box>
                    ) : category.childSiteId ? (
                      <Chip
                        icon={<BusinessIcon />}
                        label={category.childSiteName || 'Specific Site'}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ) : (
                      <Chip
                        icon={<PublicIcon />}
                        label="All Sites"
                        size="small"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.ticketCount ?? 0}
                      size="small"
                      color={(category.ticketCount ?? 0) > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(category)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={(category.ticketCount ?? 0) > 0 ? 'Cannot delete (has tickets)' : 'Delete'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(category)}
                          disabled={(category.ticketCount ?? 0) > 0}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
            helperText="e.g., Hardware, Software, Network Issues"
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            helperText="Brief description of what this category covers"
          />

          {/* Organization Scope */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Category Scope</FormLabel>
            <RadioGroup
              value={organizationScope}
              onChange={(e) => {
                const newScope = e.target.value as 'global' | 'specific'
                setOrganizationScope(newScope)
                if (newScope === 'global') {
                  setFormData({ ...formData, childSiteIds: [] })
                }
              }}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="global"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">All Sites</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Available to all sites in your tenant
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value="specific"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">Specific Site</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only visible to a specific site
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {organizationScope === 'specific' && (
            <Autocomplete
              multiple
              fullWidth
              options={organizations}
              getOptionLabel={(org) => `${org.name}${org.type ? ` (${org.type})` : ''}`}
              value={organizations.filter(org => formData.childSiteIds.includes(org.childSiteId || ''))}
              onChange={(_, newValue) => {
                setFormData({ ...formData, childSiteIds: newValue.map(org => org.childSiteId || '') })
              }}
              disabled={loadingOrgs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sites"
                  placeholder="Select one or more sites"
                  helperText="Select which sites this category should be available in"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              sx={{ mb: 3 }}
            />
          )}

          <Typography variant="subtitle2" gutterBottom>
            Category Color
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {colorOptions.map((option) => (
              <Tooltip key={option.value} title={option.name}>
                <IconButton
                  onClick={() => setFormData({ ...formData, color: option.value })}
                  sx={{
                    border: formData.color === option.value ? '3px solid black' : '1px solid #ddd',
                    borderRadius: 1,
                    p: 0.5
                  }}
                >
                  <CircleIcon sx={{ color: option.value, fontSize: 32 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CategoryManagement
